import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import './landing.scss';
import { useReviewsHoverMarquee } from './useReviewsHoverMarquee';
import { ApiConstants } from '../../constants/ApiConstants';
import { Config } from '../../config/Config';
import {
  changeLanguage,
  getAverageRating,
  getBrowserTimezone,
  getUserId,
  isAuthenticated,
  saveUsdToEurRateToStorage,
  timeAgo,
} from '../../utils/Helper';
import {
  reviewInitials,
  computeStarDistribution,
  computeAverageRatingOutOfFive,
  formatRatingOutOfFive,
  formatModalReviewCount,
} from './landingReviewsData';
import { LanguageConstants } from '../../constants/LanguageConstants';
import { countryNameForReview } from '../../utils/reviewCountryDisplay';

type UIRating = 1 | 2 | 3 | 4 | 5;

type UIReview = {
  id: number;
  name: string;
  country: string;
  stars: UIRating;
  daysAgo: string;
  text: string;
};

const formatReviewDisplayName = (fullName: string): string => {
  const tokens = fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (tokens.length === 0) return 'User';
  if (tokens.length === 1) return tokens[0];

  const firstName = tokens[0];
  const lastNameInitial = tokens[tokens.length - 1].charAt(0).toUpperCase();
  return `${firstName} ${lastNameInitial}.`;
};

function mapApiTestimonialsToReviews(list: unknown): UIReview[] {
  if (!Array.isArray(list) || list.length === 0) return [];

  return list
    .map((item: any, index: number) => {
      const details = item?.testimonial_details || {};
      const user = item?.user_details || {};
      const rawName = (user?.name || details?.title || 'User').toString().trim();
      const displayName = formatReviewDisplayName(rawName);
      const content = (details?.content || '').toString().trim();
      if (!content) return null;
      const avg = Number(getAverageRating(details)) || 0;
      const rounded = Math.max(1, Math.min(5, Math.round(avg))) as UIRating;

      const dateRaw =
        details?.updated_at ??
        details?.created_at ??
        item?.updated_at ??
        item?.created_at ??
        null;
      const daysAgo = dateRaw ? timeAgo(dateRaw) : '';

      const reviewCountryCode = details?.country_code ?? item?.country_code ?? null;

      return {
        id: Number(item?.id || details?.id || index + 1),
        name: displayName,
        country: countryNameForReview(reviewCountryCode),
        stars: rounded,
        daysAgo,
        text: content,
      };
    })
    .filter(Boolean) as UIReview[];
}

/** Load testimonials from /home/get with headers matched to how the API routes guest vs member requests. */
async function fetchHomeTestimonials(): Promise<any[]> {
  const userId = getUserId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    device_type: 'web',
    // Guest path filters testimonials with is_guest=1 (none stored). Member path returns active reviews.
    'x-is-guest-user': userId ? '1' : '0',
  };

  const token = isAuthenticated();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (userId) headers['x-user-id'] = String(userId);

  const lang = changeLanguage();
  if (lang) headers.lang = lang;

  const timezone = getBrowserTimezone();
  if (timezone) headers.timezone = timezone;

  const response = await axios.post(
    `${Config.REACT_APP_API_BASE_URL}${ApiConstants.Homepage}`,
    {},
    { headers, validateStatus: (status) => status >= 200 && status < 600 },
  );

  const body = response?.data;
  if (body?.status === true) {
    saveUsdToEurRateToStorage(body?.data?.currency_value);
    if (Array.isArray(body?.data?.testimonials)) {
      return body.data.testimonials;
    }
  }
  return [];
}

function ModalReviewText({
  text,
  expanded,
  onToggle,
}: {
  text: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const pRef = useRef<HTMLParagraphElement>(null);
  const [showToggle, setShowToggle] = useState(false);

  useLayoutEffect(() => {
    const el = pRef.current;
    if (!el) return;
    if (expanded) {
      setShowToggle(true);
      return;
    }
    setShowToggle(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <>
      <p
        ref={pRef}
        className={`landing-reviews__text ${expanded ? 'landing-reviews__text--expanded' : 'landing-reviews__text--clamped'}`}
      >
        {text}
      </p>
      {showToggle ? (
        <button type="button" className="landing-reviews__toggle" onClick={onToggle}>
          {expanded ? LanguageConstants?.LANDING_READ_LESS : LanguageConstants?.LANDING_READ_MORE}
        </button>
      ) : null}
    </>
  );
}

type Testimonial2Props = {
  /** When provided (including `[]`), uses parent homepage data and skips `/home/get`. */
  testimonials?: unknown[];
};

function Testimonial2({ testimonials }: Testimonial2Props = {}) {
  const [reviews, setReviews] = useState<UIReview[]>(() =>
    testimonials !== undefined ? mapApiTestimonialsToReviews(testimonials) : [],
  );
  const [starFilter, setStarFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (testimonials !== undefined) {
      setReviews(mapApiTestimonialsToReviews(testimonials));
      return;
    }

    let cancelled = false;

    fetchHomeTestimonials()
      .then((list) => {
        if (!cancelled) {
          setReviews(mapApiTestimonialsToReviews(list));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReviews([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [testimonials]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen]);

  /** Re-open modal with a clean filter (UX: previous star filter must not persist). */
  useEffect(() => {
    if (!modalOpen) setStarFilter('all');
  }, [modalOpen]);

  /** Collapse modal read-more state when closing (carousel keys unchanged). */
  useEffect(() => {
    if (modalOpen) return;
    setExpandedReviews((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (key.startsWith('modal-')) {
          delete next[key];
        }
      }
      return next;
    });
  }, [modalOpen]);

  const allReviews = reviews;
  const REVIEW_PREVIEW_CHAR_LIMIT = 120;
  const getReviewKey = (scope: 'carousel' | 'modal', id: number) => `${scope}-${id}`;
  const canExpandText = (text: string) => text.trim().length > REVIEW_PREVIEW_CHAR_LIMIT;
  const isExpanded = (scope: 'carousel' | 'modal', id: number) =>
    Boolean(expandedReviews[getReviewKey(scope, id)]);
  const toggleReviewExpansion = (scope: 'carousel' | 'modal', id: number) => {
    const key = getReviewKey(scope, id);
    setExpandedReviews((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const filteredModalReviews = useMemo(
    () => allReviews.filter((r) => starFilter === 'all' || String(r.stars) === starFilter),
    [allReviews, starFilter],
  );

  /** Two independent vertical columns — expanding one card only shifts cards below it in that column. */
  const modalReviewColumns = useMemo(() => {
    const left: UIReview[] = [];
    const right: UIReview[] = [];
    filteredModalReviews.forEach((r, index) => {
      if (index % 2 === 0) left.push(r);
      else right.push(r);
    });
    return { left, right };
  }, [filteredModalReviews]);

  const starDistribution = useMemo(() => computeStarDistribution(allReviews), [allReviews]);

  const averageRatingOutOfFive = useMemo(
    () => computeAverageRatingOutOfFive(allReviews),
    [allReviews],
  );
  const averageRatingLabel = useMemo(
    () => `${formatRatingOutOfFive(averageRatingOutOfFive)} out of 5`,
    [averageRatingOutOfFive],
  );

  /** Count matches list below: all reviews when filter is "all", else filtered subset. */
  const reviewCountLabel = useMemo(
    () => formatModalReviewCount(filteredModalReviews.length, LanguageConstants?.LANDING_MODAL_REVIEW_COUNT),
    [filteredModalReviews.length, LanguageConstants?.LANDING_MODAL_REVIEW_COUNT],
  );

  const starsStr = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

  const renderModalReviewCard = (r: UIReview) => (
    <article key={r.id} className="landing-reviews__card landing-reviews__card--modal">
      <div className="landing-reviews__card-head">
        <div className="landing-reviews__avatar">{reviewInitials(r.name)}</div>
        <div>
          <h3 className="landing-reviews__name">{r.name}</h3>
          <p className="landing-reviews__role">{r.country}</p>
        </div>
      </div>
      <div className="star-day-wrap">
        <div
          className="landing-reviews__stars"
          aria-label={(LanguageConstants?.LANDING_STARS_ARIA || '{n} out of 5 stars').replace(
            '{n}',
            String(r.stars),
          )}
        >
          {starsStr(r.stars)}
        </div>
        <span className="dot-separator" />
        <p className="landing-reviews__date">{r.daysAgo || '—'}</p>
      </div>
      <ModalReviewText
        text={r.text}
        expanded={isExpanded('modal', r.id)}
        onToggle={() => toggleReviewExpansion('modal', r.id)}
      />
    </article>
  );

  /** Nearest half-star (0, 0.5, …, 5) for overall row — decimals become half or whole. */
  const overallDisplayHalfStars = useMemo(() => {
    const raw = Number(averageRatingOutOfFive);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    const clamped = Math.min(5, Math.max(0, raw));
    return Math.round(clamped * 2) / 2;
  }, [averageRatingOutOfFive]);

  const OVERALL_STAR_INDEXES = [1, 2, 3, 4, 5] as const;
  const overallStarSlotFillPct = (starIndex: number): 0 | 50 | 100 => {
    const q = overallDisplayHalfStars;
    const portion = Math.min(1, Math.max(0, q - (starIndex - 1)));
    if (portion >= 1) return 100;
    if (portion >= 0.5) return 50;
    return 0;
  };

  const overallStarsAriaLabel = useMemo(() => {
    const label = (LanguageConstants?.LANDING_STARS_ARIA || '{n} out of 5 stars').replace(
      '{n}',
      formatRatingOutOfFive(overallDisplayHalfStars),
    );
    return label;
  }, [overallDisplayHalfStars, LanguageConstants?.LANDING_STARS_ARIA]);

  /** Seamless hover marquee needs a duplicated track; only enable with 3+ reviews so 1–2 stay unique. */
  const canHoverScroll = allReviews.length >= 3;
  const { trackRef, startHoverScroll, stopHoverScroll } = useReviewsHoverMarquee(canHoverScroll);
  const marqueeReviews = canHoverScroll ? [...allReviews, ...allReviews] : allReviews;

  return (
    <>
      <section id="landing-reviews" className="landing-reviews py-5">
        <div className="landing-reviews__map-bg" aria-hidden />
        <div className="container">
          <div className="landing-reviews__head">
            <p className="sec-ttl mb-0 text-capitalize">{LanguageConstants?.LANDING_REVIEWS_LABEL}</p>
            {allReviews.length >= 1 ? (
              <button type="button" className="see-more-btn" onClick={() => setModalOpen(true)}>
                {LanguageConstants?.LANDING_VIEW_ALL}
              </button>
            ) : null}
          </div>

          <div className="landing-reviews__viewport">
            {canHoverScroll ? (
              <>
                <div
                  className="landing-reviews__hover-zone landing-reviews__hover-zone--left"
                  onMouseEnter={() => startHoverScroll('left')}
                  onMouseLeave={stopHoverScroll}
                  aria-hidden
                />
                <div
                  className="landing-reviews__hover-zone landing-reviews__hover-zone--right"
                  onMouseEnter={() => startHoverScroll('right')}
                  onMouseLeave={stopHoverScroll}
                  aria-hidden
                />
              </>
            ) : null}
            <div
              ref={trackRef}
              className="landing-reviews__track landing-reviews__track--dash-marquee"
              role="list"
            >
              {marqueeReviews.map((r, idx) => (
                <article key={`${r.id}-${idx}`} className="landing-reviews__card" role="listitem" aria-hidden={idx >= allReviews.length}>
                  <div className="landing-reviews__card-head">
                    <div className="landing-reviews__avatar" aria-hidden>
                      {reviewInitials(r.name)}
                    </div>
                    <div>
                      <h3 className="landing-reviews__name">{r.name}</h3>
                      <p className="landing-reviews__role">{r.country}</p>
                    </div>
                  </div>
                  <div className="star-day-wrap">
                    <div
                      className="landing-reviews__stars"
                      aria-label={(LanguageConstants?.LANDING_STARS_ARIA || '{n} out of 5 stars').replace(
                        '{n}',
                        String(r.stars),
                      )}
                    >
                      {starsStr(r.stars)}
                    </div>
                    <span className="dot-separator" />
                    <p className="landing-reviews__date">{r.daysAgo}</p>
                  </div>
                  <p
                    className={`landing-reviews__text ${isExpanded('carousel', r.id) ? 'landing-reviews__text--expanded' : 'landing-reviews__text--clamped'}`}
                  >
                    {r.text}
                  </p>
                  {canExpandText(r.text) && (
                    <button
                      type="button"
                      className="landing-reviews__toggle"
                      onClick={() => toggleReviewExpansion('carousel', r.id)}
                    >
                      {isExpanded('carousel', r.id)
                        ? LanguageConstants?.LANDING_READ_LESS
                        : LanguageConstants?.LANDING_READ_MORE}
                    </button>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {modalOpen && allReviews.length >= 1 && (
        <div
          className="landing-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reviews-modal-title"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="landing-modal landing-modal--reviews-v2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="landing-modal__header">
              <button
                type="button"
                className="landing-modal__close"
                onClick={() => setModalOpen(false)}
                aria-label={LanguageConstants?.ARIA_CLOSE}
              >
                ×
              </button>
              <div className="landing-modal__title-wrap">
                <h2 id="reviews-modal-title" className="landing-modal__title">
                  {LanguageConstants?.LANDING_MODAL_TITLE}
                </h2>
              </div>
              <div className="landing-modal__rating-wrap">
                <div className="rating-star">
                  <div
                    className="rating-star__stars--overall"
                    role="img"
                    aria-label={overallStarsAriaLabel}
                  >
                    {OVERALL_STAR_INDEXES.map((starIndex) => {
                      const fillPct = overallStarSlotFillPct(starIndex);
                      return (
                        <span key={starIndex} className="rating-star__slot">
                          <span className="rating-star__slot-empty" aria-hidden>
                            ☆
                          </span>
                          {fillPct > 0 ? (
                            <span
                              className="rating-star__slot-fill-wrap"
                              style={{ width: `${fillPct}%` }}
                              aria-hidden
                            >
                              <span className="rating-star__slot-fill-char">★</span>
                            </span>
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                  <div className="landing-modal__overall-head gap-2">
                    <p className="landing-modal__overall-label">
                      {LanguageConstants?.LANDING_MODAL_OVERALL_RATING}
                    </p>
                    {allReviews.length > 0 ? (
                      <p className="landing-modal__overall-score" aria-label={`Average ${averageRatingLabel}`}>
                        {averageRatingLabel}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="landing-modal__rating-section">
                        <div className="landing-modal__bars-v2" aria-hidden>
                          {starDistribution.map((row) => (
                            <div key={row.star} className="landing-modal__bar-row-v2">
                              <span className="landing-modal__bar-star-label">{row.star}</span>
                              <div className="landing-modal__bar-track-v2">
                                <div
                                  className="landing-modal__bar-fill-v2"
                                  style={{ width: `${row.percent}%` }}
                                />
                              </div>
                              <span className="landing-modal__bar-pct">{row.percent}%</span>
                            </div>
                          ))}
                        </div>
                </div>
              </div>
              <div className="landing-modal__filter-bar">
                <p className="landing-modal__review-total">{reviewCountLabel}</p>
                <div className="landing-modal__filter-group">
                  <label htmlFor="star-filter">{LanguageConstants?.LANDING_FILTER_BY}</label>
                  <select
                    id="star-filter"
                    value={starFilter}
                    onChange={(e) => setStarFilter(e.target.value)}
                    aria-label={LanguageConstants?.LANDING_FILTER_ARIA}
                  >
                    <option value="all">{LanguageConstants?.LANDING_STARS_ALL}</option>
                    <option value="5">{LanguageConstants?.LANDING_STARS_5}</option>
                    <option value="4">{LanguageConstants?.LANDING_STARS_4}</option>
                    <option value="3">{LanguageConstants?.LANDING_STARS_3}</option>
                    <option value="2">{LanguageConstants?.LANDING_STARS_2}</option>
                    <option value="1">{LanguageConstants?.LANDING_STARS_1}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="landing-modal__body landing-modal__body--v2">
              <div className="landing-modal__grid">
                {filteredModalReviews.length === 0 ? (
                  <p className="landing-modal__grid-empty text-center text-muted py-4 mb-0">
                    {LanguageConstants?.REVIEWS_FILTER_NO_MATCH}
                  </p>
                ) : (
                  <>
                    <div className="landing-modal__grid-col">{modalReviewColumns.left.map(renderModalReviewCard)}</div>
                    <div className="landing-modal__grid-col">{modalReviewColumns.right.map(renderModalReviewCard)}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Testimonial2;
